import { ActionBox, OwnerInfo, SeverityEnum } from './general.interface';
import { StakeholderRoles } from './tasks.interface';
import { Comment } from './comment.interface';
import { RiskStatus } from './risk-simulation.interface';
import { ProcedureStatusEnum } from './procedures.interface';

export interface Policy {
  id: string;
  policyName: string;
  currentStatus: {
    code: string;
    suffixCode: string;
  };
  eligibleActions?: string[];
  policyStatus: PolicyStatus;
  policyCompletion?: PolicyCompletion;
  ownerInfo: OwnerInfo;
  defaultStakeholders: number;
  createdOn: string;
  approvedOn?: string;
  renewsOn?: string;
  lastModified: string;
  acceptedBy: {
    totalRequiredApproval: number;
    totalApproved: number;
  };
}

export interface ActivePolicy {
  policyId: string;
  policyName: string;
  statusId: PolicyStatus;
  currentStatus: {
    code: string;
    suffixCode: string;
  };
  statusCode: string;
  statusLabel: string;
  isActiveFull: boolean;
  isActivePartial: boolean;
  isActiveChanged: boolean;
  requiresRenewal: boolean;
  approvedControlsCount: number;
  totalControlsCount: number;
  ownerId: string;
  ownerDisplayName: string;
  stakeHolderName: string;
  additionalStakeholdersCount: number;
  activationTime: string;
  renewsOn: string;
  lastModified: string;
  totalReviewersCount: number;
  approvedReviewersCount: number;
  isReviewingActive: boolean;
  isTemplateChanged: boolean;
  renewalsAlert: boolean;
  relevantToOrg: boolean;
}

export interface PolicySummary {
  changedPoliciesCount: number;
  completionPercent: number;
  pendingPoliciesCount: number;
  totalApprovedControls: number;
  totalControlsInApprovedPolicies: number;
}

export interface ApprovePolicyResponse {
  eligibleActions: string[];
  policyStatus: PolicyStatus;
}

export interface PolicyDetails {
  id: string;
  actionBox: ActionBox;
  policyName: string; // Name of the policy
  policyDescription: string; // Description of the policy
  severity: SeverityEnum;
  currentStatus: {
    status: string; // Current status of the policy
    requiresAction: boolean; // Whether the policy requires action
    partiallyApproved: boolean; // Whether the policy is partially approved
    changedControls: boolean; // Whether the policy's controls have changed
    requiresApproval: boolean; // Whether the policy requires approval
    requiresRenewal: boolean; // Whether the policy requires renewal
    dueDate: string; // Due date of the policy in ISO 8601 format
    priority: number; // Severity or priority of the policy
  };

  controlsProgress: {
    totalControlsForPolicy: number; // Total number of controls for the policy
    totalControlApprove: number; // Total number of approved controls
  };
  approvalsProgress: {
    totalApprovals: number; // Total number of approvals required
    currentApproval: number; // Current number of approvals completed
  };
  proceduresProgress: {
    totalProcedures: number; // Total number of procedures for the policy
    proceduresApproved: number; // Total number of approved procedures
  };

  stakeholdersCount: number; // Count of stakeholders assigned to the policy
  isApprovalUser: boolean; // Whether the current user is in the approval list
  shouldApprove: boolean; // Whether the user needs to approve the policy

  stakeholders: stakeholdersList; // List of stakeholders assigned to the policy
  stakeholdersList: Stakeholder[];
  controls: {
    controlId: string;
    name: string;
    status: 'Pending' | 'Approved';
  }[];
  references: {
    risks: {
      id: string;
      status: RiskStatus;
      statusIcon?: string | null;
      name: string;
    }[];
    procedures: {
      id: string;
      status: ProcedureStatusEnum;
      name: string;
    }[];
  };
  comments: Comment[]; // List of comments assigned to the policy
  newComment?: Comment;
  policyDetailsCommentPending?: boolean;
  policyDetailsCommentFailed?: boolean;
  commentErrorMessage?: string;
}

export interface stakeholdersList {
  [StakeholderRoles.ACCOUNTABLE]: Stakeholder[];
  [StakeholderRoles.CONSULTEDS]: Stakeholder[];
  [StakeholderRoles.INFORMDES]: Stakeholder[];
  [StakeholderRoles.OWNER]: Stakeholder;
}

export interface Stakeholder {
  userId: string;
  depId: string;
  depName: string;
  userName: string;
  isCurrentUser: boolean;
  role: StakeholderRoles;
}

export enum PolicyStatus {
  PENDING_SELECTION = 1,
  ACTIVE = 2,
  ACTIVE_PARTIAL = 3,
  ACTIVE_CHANGED = 4,
  PENDING_USER_APPROVAL = 5,
  PENDING_MANAGEMENT_APPROVAL = 6,
  ACTIVE_PENDING_MANAGEMENT_APPROVAL = 7,
  ACTIVE_PENDING_SCHEDULED_APPROVAL = 8,
}

export enum PolicyStatusDisplayKey {
  PendingSelection = 'Pending Controls Selection',
  Active = 'Active',
  ActivePartial = 'Active (Partial)',
  ActiveChanged = 'Active (Changed)',
  PendingUserApproval = 'Pending User Approval',
  PendingManagementApproval = 'Pending Management Approval',
  ActivePendingManagementApproval = 'Active Pending Management Approval',
  ActivePendingScheduledApproval = 'Active Pending Scheduled Approval',
}

export interface PolicyCompletion {
  totalControls: number;
  approvedControls: number;
}
